const ORDER_ATTRIBUTE = '_order';
const ENTRY_ATTRIBUTE = '_enter';
const GA_CLIENT_ID_ATTRIBUTE = '_ga_client_id';
const GA_SESSION_ID_ATTRIBUTE = '_ga_session_id';
const GA4_COOKIE_SUFFIX = 'L65K24HNWN';
const ENTRY_COOKIE = 'baf_entry_url';
const MAX_ENTRY_LENGTH = 1500;
const GA_ATTRIBUTION_WAIT_MS = 2500;
const GA_ATTRIBUTION_POLL_MS = 100;
const CART_READ_TIMEOUT_MS = 8000;
const PORTRAIT_SYNC_TIMEOUT_MS = 12000;
const ATTRIBUTION_REQUEST_TIMEOUT_MS = 5000;
const DEFAULT_TAX_RATE = 0.21;
const TAX_RATES_BY_COUNTRY = {
  AT: 0.2,
  BE: 0.21,
  CZ: 0.21,
  DE: 0.19,
  ES: 0.21,
  FR: 0.2,
  IT: 0.22,
  NL: 0.21,
  PL: 0.23,
  SK: 0.23,
};
const TAX_RATES_BY_CURRENCY = {
  CZK: 0.21,
  EUR: 0.21,
  PLN: 0.23,
};

let syncPromise = null;
let submittingAfterSync = false;
let checkoutSyncPromise = null;

function getCookie(name) {
  const cookies = document.cookie ? document.cookie.split('; ') : [];
  const prefix = `${name}=`;

  for (const cookie of cookies) {
    if (!cookie.startsWith(prefix)) continue;

    try {
      return decodeURIComponent(cookie.slice(prefix.length));
    } catch {
      return '';
    }
  }

  return '';
}

function getAllCookies() {
  return document.cookie ? document.cookie.split('; ') : [];
}

function setCookie(name, value) {
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; SameSite=Lax${secure}`;
  } catch {
    // Ignore cookie failures; checkout can continue without attribution.
  }
}

function normalizeEntryUrl(value) {
  if (typeof value !== 'string') return '';

  const trimmed = value.trim();
  return trimmed.length > MAX_ENTRY_LENGTH ? trimmed.slice(0, MAX_ENTRY_LENGTH) : trimmed;
}

function captureEntryUrlCookie() {
  const existingEntryUrl = normalizeEntryUrl(getCookie(ENTRY_COOKIE));
  if (existingEntryUrl) return existingEntryUrl;

  const entryUrl = normalizeEntryUrl(window.location.href);
  if (entryUrl) setCookie(ENTRY_COOKIE, entryUrl);

  return entryUrl;
}

function getEntryUrlFromCookie() {
  return normalizeEntryUrl(getCookie(ENTRY_COOKIE));
}

function toStringValue(value) {
  return typeof value === 'string' ? value : '';
}

function stringValue(value) {
  return value === undefined || value === null ? '' : String(value);
}

function numberValue(value) {
  if (value === undefined || value === null || value === '') return undefined;

  const number = Number(value);
  return Number.isFinite(number) ? roundMoney(number) : undefined;
}

function positiveNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : 0;
}

function roundMoney(value) {
  return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
}

function centsToMoney(value) {
  const number = Number(value);
  return Number.isFinite(number) ? roundMoney(number / 100) : undefined;
}

function centsToNetMoney(value, taxRate) {
  const gross = centsToMoney(value);
  if (gross === undefined) return undefined;
  return roundMoney(gross / (1 + taxRate));
}

function booleanValue(value) {
  if (value === true || value === false) return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
}

function parseGaClientId(value) {
  if (typeof value !== 'string') return '';

  const parts = value.split('.');
  if (parts.length < 4) return '';

  const clientParts = parts.slice(-2);
  return clientParts.every((part) => /^\d+$/.test(part)) ? clientParts.join('.') : '';
}

function parseGaSessionId(value) {
  if (typeof value !== 'string') return '';

  const taggedSession = value.match(/(?:^|[.$])s(\d+)(?:[$.]|$)/);
  if (taggedSession) return taggedSession[1];

  const legacyParts = value.split('.');
  if (/^GS\d+$/.test(legacyParts[0]) && /^\d+$/.test(legacyParts[2] || '')) {
    return legacyParts[2];
  }

  return '';
}

function getGa4CookieValue() {
  const preferredName = `_ga_${GA4_COOKIE_SUFFIX}`;
  const preferredValue = getCookie(preferredName);
  if (preferredValue) return preferredValue;

  const cookie = getAllCookies().find((item) => /^_ga_[^=]+=GS/.test(item));
  if (!cookie) return '';

  const value = cookie.slice(cookie.indexOf('=') + 1);

  try {
    return decodeURIComponent(value);
  } catch {
    return '';
  }
}

function getGaAttribution() {
  return {
    clientId: parseGaClientId(getCookie('_ga')),
    sessionId: parseGaSessionId(getGa4CookieValue()),
  };
}

function canWaitForGaAttribution() {
  const customerPrivacy = window.Shopify?.customerPrivacy;
  if (customerPrivacy && typeof customerPrivacy.userCanBeTracked === 'function') {
    return customerPrivacy.userCanBeTracked();
  }

  return document.cookie.includes('_ga=');
}

function hasCompleteGaAttribution(gaAttribution) {
  return Boolean(gaAttribution.clientId && gaAttribution.sessionId);
}

function wait(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function getGaAttributionWhenReady(timeoutMs = GA_ATTRIBUTION_WAIT_MS) {
  let gaAttribution = getGaAttribution();

  if (hasCompleteGaAttribution(gaAttribution) || !canWaitForGaAttribution()) {
    return gaAttribution;
  }

  const deadline = Date.now() + timeoutMs;
  while (!hasCompleteGaAttribution(gaAttribution) && Date.now() < deadline) {
    await wait(GA_ATTRIBUTION_POLL_MS);
    gaAttribution = getGaAttribution();
  }

  return gaAttribution;
}

function getCartAttributes(cart) {
  if (!cart || typeof cart !== 'object') return {};

  const attributes = cart.attributes;
  return attributes && typeof attributes === 'object' ? attributes : {};
}

function getCartSummary(cart) {
  return {
    item_count: cart.item_count,
    total_price: cart.total_price,
    currency: cart.currency,
  };
}

function getTaxRate(cart) {
  const country = stringValue(window.Shopify?.country).trim().toUpperCase();
  if (country && TAX_RATES_BY_COUNTRY[country] !== undefined) return TAX_RATES_BY_COUNTRY[country];

  const currency = stringValue(cart?.currency).trim().toUpperCase();
  if (currency && TAX_RATES_BY_CURRENCY[currency] !== undefined) return TAX_RATES_BY_CURRENCY[currency];

  return DEFAULT_TAX_RATE;
}

function parseBafOrder(value) {
  const rawValue = stringValue(value).trim();
  if (!rawValue) return { items: [] };

  try {
    const parsed = JSON.parse(rawValue);
    if (!parsed || typeof parsed !== 'object') return { items: [] };

    return {
      ...parsed,
      items: Array.isArray(parsed.items)
        ? parsed.items.filter((item) => item && typeof item === 'object')
        : [],
    };
  } catch {
    return { items: [] };
  }
}

function lineItemProperties(lineItem) {
  const properties = lineItem?.properties;
  if (!properties) return {};

  if (Array.isArray(properties)) {
    return properties.reduce((values, property) => {
      if (!property || typeof property !== 'object') return values;

      const key = stringValue(property.name || property.key);
      if (!key) return values;

      values[key] = property.value;
      return values;
    }, {});
  }

  if (typeof properties === 'object') return properties;

  return {};
}

function findUnusedIndex(items, usedIndexes, predicate) {
  const index = items.findIndex((item, itemIndex) => !usedIndexes.has(itemIndex) && predicate(item));
  return index >= 0 ? index : undefined;
}

function isArtPrintLineItem(lineItem, properties = lineItemProperties(lineItem)) {
  return stringValue(properties._baf_order_type) === 'art_print' ||
    stringValue(lineItem?.product_type || lineItem?.product?.type).trim().toLowerCase() === 'art prints' ||
    (Array.isArray(lineItem?.product?.tags) && lineItem.product.tags.includes('art-prints'));
}

function createBafOrderItemMatcher(bafOrder) {
  const items = Array.isArray(bafOrder?.items) ? bafOrder.items : [];
  const usedIndexes = new Set();

  return (lineItem, index) => {
    const properties = lineItemProperties(lineItem);
    // Ready-made art uses native variant identity, never a portrait/merch entry at the same index.
    if (isArtPrintLineItem(lineItem, properties)) return undefined;
    const orderItemId = stringValue(properties._baf_order_item_id);
    const printfulVariantId = stringValue(properties._baf_printful_variant_id);
    const orderType = stringValue(properties._baf_order_type);

    const matchedIndex = findUnusedIndex(items, usedIndexes, (item) => {
      return orderItemId && stringValue(item.order_item_id) === orderItemId;
    }) ?? findUnusedIndex(items, usedIndexes, (item) => {
      return printfulVariantId && stringValue(item.printful_variant_id) === printfulVariantId;
    }) ?? findUnusedIndex(items, usedIndexes, (item) => {
      return orderType && stringValue(item.type) === orderType;
    }) ?? (!usedIndexes.has(index) && items[index] ? index : undefined);

    if (matchedIndex === undefined) return undefined;

    usedIndexes.add(matchedIndex);
    return items[matchedIndex];
  };
}

function joinNonEmpty(values, separator) {
  return values.map((value) => stringValue(value).trim()).filter(Boolean).join(separator);
}

function removeEmpty(object) {
  return Object.fromEntries(
    Object.entries(object).filter(([, value]) => {
      if (value === undefined || value === null || value === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    })
  );
}

function itemIdValue(lineItem, properties, bafOrderItem) {
  if (isArtPrintLineItem(lineItem, properties)) {
    return stringValue(lineItem?.variant_id || lineItem?.id || lineItem?.sku || lineItem?.product_id);
  }

  return stringValue(
    bafOrderItemIdValue(bafOrderItem) ||
      productionPropertyItemIdValue(properties) ||
      lineItem?.sku ||
      lineItem?.variant_id ||
      lineItem?.product_id ||
      lineItem?.id
  );
}

function bafOrderItemIdValue(item) {
  if (!item || typeof item !== 'object') return '';

  const explicitItemId = stringValue(item.item_id);
  if (explicitItemId) return explicitItemId;

  const printfulVariantId = stringValue(item.printful_variant_id);
  if (printfulVariantId) return printfulVariantId;

  return stringValue(item.order_item_id);
}

function productionPropertyItemIdValue(properties) {
  const printfulVariantId = stringValue(properties._baf_printful_variant_id);
  if (printfulVariantId) return printfulVariantId;

  return stringValue(properties._baf_order_item_id);
}

function itemNameValue(lineItem, bafOrderItem) {
  const properties = lineItemProperties(lineItem);
  if (isArtPrintLineItem(lineItem, properties)) {
    // Older carts retain former family labels; their native variant identity stays unchanged.
    switch (stringValue(properties._baf_art_id)) {
      case '18': return 'Starry Walk · Golden Retriever';
      case '19': return 'Starry Walk · Italian Greyhound';
      case '20': return 'Starry Walk · Labrador';
      case '34': return 'The Treat Smile · Labrador';
    }
    return stringValue(properties._baf_display_product_title) ||
      joinNonEmpty([properties.Artwork, properties.Edition], ' · ') ||
      stringValue(lineItem?.product_title || lineItem?.title || lineItem?.name);
  }
  const type = stringValue(bafOrderItem?.type);

  if (type === 'portrait') {
    return stringValue(bafOrderItem.style_name || bafOrderItem.style_id || lineItem?.product_title || lineItem?.title || lineItem?.name);
  }

  if (type === 'merch') {
    return stringValue(bafOrderItem.design_name || bafOrderItem.production_name || lineItem?.product_title || lineItem?.title || lineItem?.name);
  }

  return stringValue(lineItem?.product_title || lineItem?.title || lineItem?.name);
}

function itemVariantValue(lineItem, bafOrderItem) {
  const type = stringValue(bafOrderItem?.type);

  if (type === 'portrait') {
    return joinNonEmpty([bafOrderItem.product_type, bafOrderItem.size], ' / ') || stringValue(lineItem?.variant_title);
  }

  if (type === 'merch') {
    return joinNonEmpty([bafOrderItem.product_name, bafOrderItem.product_color, bafOrderItem.size], ' / ') ||
      stringValue(lineItem?.variant_title);
  }

  return stringValue(lineItem?.variant_title);
}

function itemCategoryValue(lineItem, bafOrderItem) {
  if (isArtPrintLineItem(lineItem)) return 'Art Prints';
  const type = stringValue(bafOrderItem?.type);
  if (type === 'portrait') return 'portrait';
  if (type === 'merch') return 'merch';
  return stringValue(lineItem?.product_type || lineItem?.product_category || lineItem?.category);
}

function photoCountValue(photos) {
  if (!Array.isArray(photos)) return undefined;
  return photos.length;
}

function centsPropertyValue(value) {
  const cents = numberValue(value);
  return cents == null ? undefined : roundMoney(cents / 100);
}

function placementsValue(placements) {
  if (!Array.isArray(placements)) return undefined;

  const value = placements
    .map((placement) => {
      if (!placement || typeof placement !== 'object') return '';
      return joinNonEmpty([
        placement.placement_key || placement.placement,
        placement.artwork || placement.artwork_file,
        placement.embroidery_type_key || placement.embroidery_type,
      ], ':');
    })
    .filter(Boolean)
    .join('|');

  return value || undefined;
}

function portraitItemParams(item, properties) {
  const supportAmount = numberValue(item.support_amount) ?? centsPropertyValue(properties._donation);

  return removeEmpty({
    baf_framing: stringValue(item.framing),
    baf_frame_color: stringValue(item.frame_color),
    baf_pasparta: booleanValue(item.pasparta),
    baf_photo_count: photoCountValue(item.photos),
    baf_low_resolution: booleanValue(item.low_resolution),
    baf_skip_the_queue: booleanValue(item.skip_the_queue),
    baf_support_amount: supportAmount,
  });
}

function merchItemParams(item, taxRate) {
  const production = item.production && typeof item.production === 'object' ? item.production : {};
  const thread = production.thread && typeof production.thread === 'object' ? production.thread : {};
  const discount = item.discount && typeof item.discount === 'object' ? item.discount : {};
  const discountSalePrice = centsToNetMoney(discount.sale_price, taxRate);
  const discountCompareAtPrice = centsToNetMoney(discount.compare_at_price, taxRate);

  return removeEmpty({
    baf_production_line: stringValue(item.production_line),
    baf_artwork_colorway: stringValue(item.artwork_colorway),
    baf_production_technique: stringValue(production.technique),
    baf_production_method: stringValue(production.printing_type),
    baf_thread_code: stringValue(thread.code),
    baf_placements: placementsValue(production.placements),
    baf_discount_type: stringValue(discount.type),
    baf_discount_source: stringValue(discount.source),
    baf_discount_sale_price: discountSalePrice,
    baf_discount_compare_at_price: discountCompareAtPrice,
    baf_discount_amount:
      discountCompareAtPrice !== undefined && discountSalePrice !== undefined && discountCompareAtPrice > discountSalePrice
        ? roundMoney(discountCompareAtPrice - discountSalePrice)
        : undefined,
  });
}

function bafDiscountValue(bafOrderItem, taxRate) {
  const discount = bafOrderItem?.discount;
  if (!discount || typeof discount !== 'object') return undefined;

  const salePrice = centsToNetMoney(discount.sale_price, taxRate);
  const compareAtPrice = centsToNetMoney(discount.compare_at_price, taxRate);

  return compareAtPrice !== undefined && salePrice !== undefined && compareAtPrice > salePrice
    ? roundMoney(compareAtPrice - salePrice)
    : undefined;
}

function bafItemParams(bafOrderItem, properties, taxRate) {
  if (!bafOrderItem || typeof bafOrderItem !== 'object') return {};

  const type = stringValue(bafOrderItem.type);
  if (type === 'portrait') return portraitItemParams(bafOrderItem, properties);
  if (type === 'merch') return merchItemParams(bafOrderItem, taxRate);

  return {};
}

function matchingCartLineIndex(cartLines, lineItem) {
  if (!lineItem || typeof lineItem !== 'object') return undefined;

  const key = stringValue(lineItem.key);
  if (key) {
    const matchedIndex = cartLines.findIndex((cartLine) => stringValue(cartLine?.key) === key);
    if (matchedIndex >= 0) return matchedIndex;
  }

  const id = stringValue(lineItem.id);
  const variantId = stringValue(lineItem.variant_id || lineItem.variantId);
  const matchedIndex = cartLines.findIndex((cartLine) => {
    return (id && stringValue(cartLine?.id) === id) || (variantId && stringValue(cartLine?.variant_id) === variantId);
  });

  return matchedIndex >= 0 ? matchedIndex : undefined;
}

function selectedCartLines(cart, options = {}) {
  const cartLines = Array.isArray(cart?.items) ? cart.items : [];
  const lineItem = options.lineItem && typeof options.lineItem === 'object' ? options.lineItem : undefined;

  if (!lineItem) {
    return cartLines.map((cartLine, index) => ({
      lineItem: cartLine,
      orderIndex: index,
    }));
  }

  const matchedIndex = matchingCartLineIndex(cartLines, lineItem);
  const selectedLine = matchedIndex === undefined ? lineItem : cartLines[matchedIndex];
  const quantity = positiveNumber(lineItem.quantity) || positiveNumber(selectedLine.quantity) || 1;

  return [{
    lineItem: { ...selectedLine, quantity },
    orderIndex: matchedIndex ?? 0,
  }];
}

function buildEcommerceItem(lineItem, index, bafOrderItem, taxRate) {
  const properties = lineItemProperties(lineItem);
  const grossPriceCents = Number.isFinite(Number(lineItem?.final_price)) ? lineItem.final_price : lineItem?.price;
  const originalPriceCents = Number.isFinite(Number(lineItem?.price)) ? lineItem.price : grossPriceCents;
  const price = centsToNetMoney(grossPriceCents, taxRate);
  const originalPrice = centsToNetMoney(originalPriceCents, taxRate);
  const lineDiscount = originalPrice !== undefined && price !== undefined && originalPrice > price
    ? roundMoney(originalPrice - price)
    : undefined;
  const discount = lineDiscount ?? bafDiscountValue(bafOrderItem, taxRate);

  return removeEmpty({
    item_id: itemIdValue(lineItem, properties, bafOrderItem),
    item_name: itemNameValue(lineItem, bafOrderItem),
    item_variant: itemVariantValue(lineItem, bafOrderItem),
    item_category: itemCategoryValue(lineItem, bafOrderItem),
    index,
    price,
    discount,
    quantity: positiveNumber(lineItem?.quantity) || 1,
    ...bafItemParams(bafOrderItem, properties, taxRate),
    ...(isArtPrintLineItem(lineItem, properties) ? {
      baf_art_id: stringValue(properties._baf_art_id),
      baf_art_material: stringValue(properties._baf_art_material),
      baf_art_frame: stringValue(properties._baf_art_frame),
    } : {}),
  });
}

function ecommerceValue(items) {
  if (!Array.isArray(items) || items.length === 0) return undefined;

  let hasPrice = false;
  const total = items.reduce((sum, item) => {
    const price = numberValue(item.price);
    if (price === undefined) return sum;

    hasPrice = true;
    return sum + price * (positiveNumber(item.quantity) || 1);
  }, 0);

  return hasPrice ? roundMoney(total) : undefined;
}

function buildCartEcommerce(cart, options = {}) {
  const attributes = getCartAttributes(cart);
  const bafOrder = parseBafOrder(attributes[ORDER_ATTRIBUTE]);
  const matchBafOrderItem = createBafOrderItemMatcher(bafOrder);
  const taxRate = getTaxRate(cart);
  const items = selectedCartLines(cart, options).map((selection, index) => {
    return buildEcommerceItem(
      selection.lineItem,
      index,
      matchBafOrderItem(selection.lineItem, selection.orderIndex),
      taxRate
    );
  });

  return {
    currency: stringValue(cart?.currency),
    value: ecommerceValue(items),
    items,
  };
}

function hasCheckoutTarget() {
  return Boolean(document.querySelector('#checkout, form[action="/cart"] [name="checkout"]'));
}

function withCheckoutDeadline(operation, timeout, message) {
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  let timer;
  return Promise.race([
    Promise.resolve().then(() => operation(controller?.signal)),
    new Promise((_, reject) => {
      timer = window.setTimeout(() => {
        const error = new Error(message);
        error.code = 'checkout-timeout';
        reject(error);
        controller?.abort();
      }, timeout);
    }),
  ]).finally(() => window.clearTimeout(timer));
}

function fetchCheckoutJson(url, options, timeout) {
  return withCheckoutDeadline(async (signal) => {
    const response = await fetch(url, { ...options, signal });
    if (!response.ok) throw new Error('Could not update checkout.');
    return response.json();
  }, timeout, 'Checkout is taking longer than expected. Please try again.');
}

async function syncGaAttributionAttributes(waitMs = GA_ATTRIBUTION_WAIT_MS, requestTimeout = ATTRIBUTION_REQUEST_TIMEOUT_MS) {
  const gaAttribution = await getGaAttributionWhenReady(waitMs);
  const entryUrl = getEntryUrlFromCookie();
  const attributes = {};

  if (gaAttribution.clientId) attributes[GA_CLIENT_ID_ATTRIBUTE] = gaAttribution.clientId;
  if (gaAttribution.sessionId) attributes[GA_SESSION_ID_ATTRIBUTE] = gaAttribution.sessionId;
  if (entryUrl) attributes[ENTRY_ATTRIBUTE] = entryUrl;

  if (!Object.keys(attributes).length) return null;

  return fetchCheckoutJson('/cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      attributes,
    }),
  }, requestTimeout);
}

function fetchCart() {
  return fetchCheckoutJson('/cart.js', {
    headers: { Accept: 'application/json' },
  }, CART_READ_TIMEOUT_MS);
}

function scheduleEntrySync() {
  if (syncPromise) return syncPromise;

  syncPromise = syncGaAttributionAttributes()
    .catch((error) => {
      console.error(error);
    })
    .finally(() => {
      syncPromise = null;
    });

  return syncPromise;
}

function handleReady() {
  if (hasCheckoutTarget() || canWaitForGaAttribution()) scheduleEntrySync();
}

function isCheckoutSubmit(event) {
  const submitter = event.submitter;
  if (submitter instanceof HTMLButtonElement || submitter instanceof HTMLInputElement) {
    return submitter.name === 'checkout' || submitter.id === 'checkout';
  }

  return event.target instanceof HTMLFormElement && Boolean(event.target.querySelector('[name="checkout"], #checkout'));
}

function submitCheckoutForm(form, submitter) {
  if (submitter instanceof HTMLElement && typeof form.requestSubmit === 'function') {
    form.requestSubmit(submitter);
    return;
  }

  if (typeof form.requestSubmit === 'function') {
    form.requestSubmit();
    return;
  }

  let checkoutInput = form.querySelector('input[name="checkout"][data-baf-entry-submit]');
  if (!(checkoutInput instanceof HTMLInputElement)) {
    checkoutInput = document.createElement('input');
    checkoutInput.type = 'hidden';
    checkoutInput.name = 'checkout';
    checkoutInput.value = 'Checkout';
    checkoutInput.dataset.bafEntrySubmit = 'true';
    form.appendChild(checkoutInput);
  }

  form.submit();
}

function publishBeginCheckout(cart) {
  if (!window.Shopify?.analytics || typeof window.Shopify.analytics.publish !== 'function') return;

  const attributes = getCartAttributes(cart);
  const ecommerce = buildCartEcommerce(cart);

  window.Shopify.analytics.publish('baf_begin_checkout', {
    ...ecommerce,
    _Order: toStringValue(attributes[ORDER_ATTRIBUTE]),
    _Enter: toStringValue(attributes[ENTRY_ATTRIBUTE]) || getEntryUrlFromCookie(),
    cart: getCartSummary(cart),
  });
}

function syncGaAttributionAndPublishBeginCheckout(cart) {
  // Attribution is best effort and must not hold checkout on a slow connection.
  return syncGaAttributionAttributes(0, 1500)
    .catch((error) => {
      console.warn('Checkout attribution skipped:', error);
    })
    .then(() => {
      try { publishBeginCheckout(cart); } catch (error) {
        console.warn('Begin checkout analytics publish failed:', error);
      }
    });
}

async function prepareCheckoutCart() {
  const pendingPortrait = window.BAFPendingPortrait;
  if (typeof pendingPortrait?.assertReadyForCheckout === 'function') {
    // A staged portrait is not a Shopify line yet; check before reading the cart.
    await pendingPortrait.assertReadyForCheckout();
  } else {
    let hasPendingPortrait = false;
    try { hasPendingPortrait = window.localStorage?.getItem('baf_pending_portrait') === '1'; } catch {}
    if (hasPendingPortrait) {
      const error = new Error('Your saved photo is still uploading. Please reload this page before checkout.');
      error.code = 'portrait-pending';
      throw error;
    }
  }
  const cart = await fetchCart();
  const hasPortrait = Array.isArray(cart.items) && cart.items.some((item) => lineItemProperties(item)._baf_order_type === 'portrait');
  const hasArtPrint = Array.isArray(cart.items) && cart.items.some((item) => isArtPrintLineItem(item));
  if (!hasPortrait && !hasArtPrint) return cart;
  return withCheckoutDeadline(async (signal) => {
    // A first checkout can race the deferred cart scripts on a slow phone.
    const syncCartOrder = typeof window.BAFOrderSync?.syncCartOrder === 'function'
      ? window.BAFOrderSync.syncCartOrder
      : (await import('@theme/baf-order-sync')).syncBafOrderAttribute;
    if (signal?.aborted) throw new Error('Order sync was cancelled.');
    if (typeof syncCartOrder !== 'function') throw new Error('Order sync is unavailable.');
    return syncCartOrder({ cart, requirePortraitData: true, signal });
  }, PORTRAIT_SYNC_TIMEOUT_MS, 'Your order details could not be saved. Please try checkout again.');
}

function showCheckoutSyncError(form, submitter, error) {
  let message = form._bafCheckoutSyncError;
  if (!message || !message.isConnected) {
    message = document.createElement('p');
    message.className = 'baf-checkout-error';
    message.setAttribute('role', 'alert');
    const container = submitter?.closest?.('.cart__ctas') || form;
    container.appendChild(message);
    form._bafCheckoutSyncError = message;
  }
  message.textContent = error?.code === 'portrait-pending'
    ? error.message
    : error?.code === 'portrait-data-missing'
    ? 'Your portrait details could not be confirmed. Please remove the portrait from your cart and add it again.'
    : error?.code === 'art-print-data-missing'
    ? error.message
    : 'We couldn’t prepare checkout. Please check your connection and tap Secure Checkout again.';
  message.hidden = false;
}

captureEntryUrlCookie();

document.addEventListener('cart:update', (event) => {
  if (event.detail?.data?.analyticsSyncHandled === true) return;
  scheduleEntrySync();
});

document.addEventListener('trackingConsentAccepted', () => {
  scheduleEntrySync();
});

window.BAFEntrySync = {
  syncEntryAttribute: scheduleEntrySync,
  getEntryUrl: getEntryUrlFromCookie,
};

window.BAFEcommerceAnalytics = {
  buildCartEcommerce,
};

document.addEventListener(
  'submit',
  (event) => {
    if (submittingAfterSync) {
      submittingAfterSync = false;
      return;
    }

    if (!isCheckoutSubmit(event)) return;

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    event.preventDefault();
    if (checkoutSyncPromise) return;
    const submitter = event.submitter;
    const button = submitter instanceof HTMLButtonElement || submitter instanceof HTMLInputElement ? submitter : null;
    const wasDisabled = button?.disabled;
    if (form._bafCheckoutSyncError) form._bafCheckoutSyncError.hidden = true;
    if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
    const restoreButton = () => {
      if (button) { button.disabled = Boolean(wasDisabled); button.removeAttribute('aria-busy'); }
    };
    checkoutSyncPromise = prepareCheckoutCart()
      .then((cart) => syncGaAttributionAndPublishBeginCheckout(cart))
      .then(() => {
        restoreButton();
        submittingAfterSync = true;
        try { submitCheckoutForm(form, submitter); }
        finally { submittingAfterSync = false; }
      })
      .catch((error) => {
        submittingAfterSync = false;
        showCheckoutSyncError(form, submitter, error);
      })
      .finally(() => {
        restoreButton();
        checkoutSyncPromise = null;
      });
  },
  true
);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', handleReady, { once: true });
} else {
  handleReady();
}
